/**
 * useBenefitsSlider — Benefits 슬라이드 페이지네이션 로직
 *
 * 원본 preview/index.html JS 그대로:
 *  - 5개/페이지, 총 10개 = 2페이지 (데스크톱)
 *  - autoplay 5초, viewport hover 시 일시정지
 *  - prev/next/dot 클릭 시 즉시 이동 + 타이머 리셋
 *
 * 반응형:
 *  - 1280 미만: 3개/페이지 → 4페이지
 *  - 768 미만: 1개/페이지 → 10페이지 (실질 스와이프 위주)
 *
 * 트랙 스타일:
 *  translateX(-{current * 100}% - {current * gap}px)
 *  → CSS의 .benefits-track { display:flex; gap:24px } 와 정확히 맞물림
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseBenefitsSliderOptions {
  totalCards: number;
  /** 자동 슬라이드 간격 ms (0이면 비활성). 기본 5000 */
  intervalMs?: number;
}

export interface UseBenefitsSliderResult {
  /** 현재 페이지 인덱스 (0-based) */
  current: number;
  /** 총 페이지 수 */
  pageCount: number;
  /** 한 페이지당 카드 수 (반응형으로 변함) */
  perPage: number;
  /** 트랙에 적용할 translateX style */
  trackStyle: React.CSSProperties;
  /** 화살표/도트 클릭 핸들러 */
  goTo: (idx: number) => void;
  prev: () => void;
  next: () => void;
  /** viewport에 onMouseEnter/Leave 바인딩 → autoplay 제어 */
  bindViewport: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
}

/** 화면 폭으로 perPage 결정 (CSS의 .benefit-card flex 값과 일치) */
function getPerPage(): number {
  if (typeof window === 'undefined') return 5;
  const w = window.innerWidth;
  if (w <= 767) return 1;
  if (w <= 1279) return 3;
  return 5;
}

export function useBenefitsSlider({
  totalCards,
  intervalMs = 5000,
}: UseBenefitsSliderOptions): UseBenefitsSliderResult {
  const [perPage, setPerPage] = useState<number>(() => getPerPage());
  const [current, setCurrent] = useState(0);
  const timerRef = useRef<number | null>(null);
  const isHoveredRef = useRef(false);

  const pageCount = Math.max(1, Math.ceil(totalCards / perPage));

  // 화면 리사이즈 시 perPage 갱신 + 페이지 인덱스 보정
  useEffect(() => {
    const onResize = () => {
      const next = getPerPage();
      setPerPage((prev) => (prev === next ? prev : next));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // perPage 바뀌면 현재 인덱스를 새 페이지 범위로 클램프
  useEffect(() => {
    setCurrent((c) => Math.min(c, Math.max(0, Math.ceil(totalCards / perPage) - 1)));
  }, [perPage, totalCards]);

  const stop = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const goTo = useCallback((idx: number) => {
    setCurrent((_c) => ((idx % pageCount) + pageCount) % pageCount);
  }, [pageCount]);

  const next = useCallback(() => setCurrent((c) => (c + 1) % pageCount), [pageCount]);
  const prev = useCallback(() => setCurrent((c) => (c - 1 + pageCount) % pageCount), [pageCount]);

  const start = useCallback(() => {
    stop();
    if (intervalMs <= 0) return;
    if (isHoveredRef.current) return;
    timerRef.current = window.setInterval(() => {
      setCurrent((c) => (c + 1) % pageCount);
    }, intervalMs);
  }, [intervalMs, pageCount, stop]);

  // 마운트 시 시작, pageCount 변경/언마운트 시 정리
  useEffect(() => {
    start();
    return stop;
  }, [start, stop]);

  // 사용자가 prev/next/goTo 누르면 타이머 리셋 (current 변경 감지)
  // current 변경 시 타이머만 재시작 (페이지 점프 효과 + 일정 간격 유지)
  useEffect(() => {
    start();
    return stop;
  }, [current, start, stop]);

  const bindViewport = {
    onMouseEnter: () => {
      isHoveredRef.current = true;
      stop();
    },
    onMouseLeave: () => {
      isHoveredRef.current = false;
      start();
    },
  };

  // gap 24px (1279 이하에서도 동일, 767 이하는 14px) → 페이지 단위 이동량
  const gapPx = perPage === 1 ? 14 : 24;
  const trackStyle: React.CSSProperties = {
    transform: `translateX(calc(-${current * 100}% - ${current * gapPx}px))`,
  };

  return {
    current,
    pageCount,
    perPage,
    trackStyle,
    goTo,
    prev,
    next,
    bindViewport,
  };
}
